/**
 * A navegação do sistema.
 *
 * Cada item declara a PERMISSÃO que o torna visível, não o papel. É a razão
 * de a matriz da spec 04 ser por chave: dividir uma tela em duas, ou juntar
 * duas em uma, não deveria obrigar ninguém a mexer no controle de acesso.
 *
 * E, para deixar registrado: esconder o item não protege nada
 * (CONSTITUICAO.md §4.1). Quem decide é o porteiro, no servidor, a cada
 * requisição. Isto aqui é só para a pessoa não ver uma porta que não abre.
 */
import type { NomeIcone } from '../../componentes/Icone'

export type ItemNav = {
  rotulo: string
  icone: NomeIcone
  destino: string
  permissao: string
  /**
   * `false` enquanto a tela não existe. O item aparece na lista, apagado e
   * sem navegar — mostrar o mapa do produto é útil, e um link que leva a
   * lugar nenhum não é.
   */
  pronta: boolean
  /**
   * `true` quando o destino precisa sobreviver à navegação de volta ao
   * back-office. É o caso do PDV: o turno fica aberto na aba enquanto o
   * operador consulta relatório ou estoque na mesma sessão, sem perder o
   * carrinho em andamento — o mesmo desenho que Square e Vend usam ao
   * separar o terminal de venda do dashboard administrativo.
   */
  abrirEmNovaAba?: boolean
}

export type GrupoNav = {
  titulo: string
  itens: ItemNav[]
}

export const NAVEGACAO: GrupoNav[] = [
  {
    titulo: 'Operação',
    itens: [
      { rotulo: 'Dashboard', icone: 'space_dashboard', destino: '/app', permissao: 'dashboard.read', pronta: true },
      {
        rotulo: 'PDV',
        icone: 'point_of_sale',
        destino: '/app/pdv',
        permissao: 'sale.create',
        pronta: true,
        abrirEmNovaAba: true,
      },
      { rotulo: 'Caixa', icone: 'storefront', destino: '/app/caixa', permissao: 'cash.shift', pronta: true },
      // Mesma permissão do PDV, de propósito (instrução do dono do produto):
      // é uma venda em potencial, a mesma pessoa que vende no balcão abre a
      // condicional — não é 403 surpresa para quem já usa o PDV.
      {
        rotulo: 'Condicional',
        icone: 'apparel',
        destino: '/app/condicionais',
        permissao: 'sale.create',
        pronta: true,
      },
    ],
  },
  {
    titulo: 'Catálogo',
    itens: [
      { rotulo: 'Estoque', icone: 'inventory_2', destino: '/app/estoque', permissao: 'stock.read', pronta: true },
      // A chave é a da LEITURA, não a da escrita. Gatilhar o item em
      // `product.write` esconderia o catálogo do caixa e do vendedor, que
      // podem consultá-lo — a rota GET exige `stock.read`. O botão "Novo
      // produto" some sozinho por `product.write`, dentro da tela.
      { rotulo: 'Produtos', icone: 'sell', destino: '/app/produtos', permissao: 'stock.read', pronta: true },
      // O protótipo põe Etiquetas como sub-item de Produtos, num menu que
      // abre. Esta navegação é PLANA — e um nível de dobra existindo só para
      // um item seria mais mecanismo do que o mapa do produto justifica. Ela
      // entra ao lado de Produtos, e a trilha da tela ("Produtos / Etiquetas")
      // conta a hierarquia que o menu não desenha.
      //
      // Mesma chave de Produtos: quem consulta o catálogo etiqueta a peça.
      // Imprimir etiqueta não escreve nada no banco.
      { rotulo: 'Etiquetas', icone: 'barcode_scanner', destino: '/app/produtos/etiquetas', permissao: 'stock.read', pronta: true },
      // Compras é a NOTA FISCAL DE COMPRA, e nada mais. O pedido de compra —
      // a intenção de comprar registrada antes de a mercadoria chegar — saiu
      // do menu junto com as duas telas-vitrine que o mostravam só de
      // travessão: um item que existe no menu e não faz nada ensina que a
      // função existe e depois não leva a lugar nenhum (ver docs/de-para.md).
      {
        rotulo: 'Compras',
        icone: 'shopping_cart',
        destino: '/app/compras/notas-fiscais',
        permissao: 'purchase.create',
        pronta: true,
      },
    ],
  },
  {
    titulo: 'Gestão',
    itens: [
      // A spec 04 não tem chave própria para cliente. Quem vende precisa
      // identificar quem está comprando, então a de venda é a que descreve o
      // acesso hoje — se um dia cadastro de cliente virar tela restrita, ela
      // ganha chave e este item passa a apontar para ela.
      { rotulo: 'Clientes', icone: 'person', destino: '/app/clientes', permissao: 'sale.create', pronta: true },
      // Fornecedor é cadastro à parte de cliente (mesma forma, papel de
      // negócio oposto) — por isso item próprio, e não uma aba dentro de
      // Compras. A permissão é a mesma de Compras (`purchase.create`): não
      // existe papel que compre sem poder ver quem fornece.
      { rotulo: 'Fornecedores', icone: 'local_shipping', destino: '/app/fornecedores', permissao: 'purchase.create', pronta: true },
      { rotulo: 'Relatórios', icone: 'bar_chart', destino: '/app/relatorios', permissao: 'finance.reports', pronta: true },
      { rotulo: 'Financeiro', icone: 'payments', destino: '/app/financeiro', permissao: 'finance.payables', pronta: true },
      { rotulo: 'Fiscal', icone: 'receipt_long', destino: '/app/fiscal', permissao: 'fiscal.issue', pronta: true },
      { rotulo: 'Configurações', icone: 'settings', destino: '/app/configuracoes', permissao: 'settings.fiscal', pronta: true },
    ],
  },
]
